import React, { useState, useEffect } from 'react';
import { fetchCards } from '../../../../../Actions/CardsActions';

const Cards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const loadCards = async (currentPage, searchTerm) => {
    try {
      setLoading(true);
      const response = await fetchCards(currentPage, limit, searchTerm);
      const list = response.cards || response.card_list || response.results || [];
      const totalCount = (response.total ?? response.count ?? response.total_count ?? list.length ?? 0);
      setCards(list);
      setTotal(totalCount);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeoutId = setTimeout(() => {
      loadCards(page, search);
    }, 500);

    setSearchTimeout(timeoutId);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [page, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (_) {
      return dateString;
    }
  };

  const handleCopyLink = async (link, id) => {
    if (!link) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) {
      // ignore copy error
    }
  };

  const getCardStatusLabel = (card) => {
    const normalize = (value) => {
      if (typeof value === 'string') return value.toLowerCase();
      return value;
    };
    const status = normalize(card.status ?? card.state ?? card.card_status);
    const isDeleted = card.is_deleted === true || status === 'deleted';
    if (isDeleted) return 'Deleted';
    const isDisabled = card.is_disabled === true || card.is_active === false || status === 'disabled';
    if (isDisabled) return 'Disabled';
    return 'Active';
  };

  const getStatusChipClasses = (label) => {
    switch (label) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Disabled':
        return 'bg-gray-100 text-gray-800';
      case 'Deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Cards</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Search cards..."
            value={search}
            onChange={handleSearchChange}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[1000px] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Card ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Form ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cards.map((card) => (
                    <tr key={card.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{card.user_full_name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap w-40">
                        <div className="max-w-[10rem] truncate">{card.id || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{card.form_id || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {card.link ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleCopyLink(card.link, card.id)}
                              className="px-3 py-1 border rounded-md bg-[#118B91] text-white hover:bg-[#118B91]/80"
                            >
                              Copy Link
                            </button>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{formatDateTime(card.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {(() => {
                          const label = getCardStatusLabel(card);
                          return (
                            <span className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-large font-bold ${getStatusChipClasses(label)}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className={`px-4 py-2 border rounded-md ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * limit >= total}
                  className={`px-4 py-2 border rounded-md ${page * limit >= total ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cards;


